import * as React from "react"
import * as SliderPrimitive from "@radix-ui/react-slider"
import { cva, type VariantProps } from "class-variance-authority"
import { cn } from "../../../lib/utils"

const sliderVariants = cva("relative flex w-full touch-none select-none items-center", {
  variants: {
    size: {
      sm: "[&_[data-slot=track]]:h-[3px]",
      md: "[&_[data-slot=track]]:h-[4px]",
      lg: "[&_[data-slot=track]]:h-[5px]",
    },
  },
  defaultVariants: {
    size: "md",
  },
})

export interface SliderProps
  extends React.ComponentPropsWithoutRef<typeof SliderPrimitive.Root>,
    VariantProps<typeof sliderVariants> {
  label?: string
  showValue?: boolean
  showTooltip?: boolean
  showMinMax?: boolean
}

const Slider = React.forwardRef<
  React.ElementRef<typeof SliderPrimitive.Root>,
  SliderProps
>(({
  className,
  size,
  label,
  showValue = false,
  showTooltip = false,
  showMinMax = false,
  disabled,
  value,
  defaultValue,
  min = 0,
  max = 100,
  onValueChange,
  ...props
}, ref) => {
  const currentValue = value ?? defaultValue ?? [min]

  const currentMin = Array.isArray(currentValue) ? (currentValue as number[])[0] : min
  const currentMax = Array.isArray(currentValue) && (currentValue as number[]).length === 2
    ? (currentValue as number[])[1]
    : max

  const thumbPct = (v: number) =>
    max === min ? 0 : ((v - min) / (max - min)) * 100

  const [minInputVal, setMinInputVal] = React.useState(String(currentMin))
  const [maxInputVal, setMaxInputVal] = React.useState(String(currentMax))

  React.useEffect(() => {
    setMinInputVal(String(currentMin))
    setMaxInputVal(String(currentMax))
  }, [currentMin, currentMax])

  const allowNumericOnly = (raw: string) => /^\d*\.?\d*$/.test(raw) || raw === ""

  const handleMinChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (allowNumericOnly(e.target.value)) setMinInputVal(e.target.value)
  }

  const handleMaxChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (allowNumericOnly(e.target.value)) setMaxInputVal(e.target.value)
  }

  const commitMin = () => {
    const parsed = Number(minInputVal)
    if (minInputVal.trim() === "" || isNaN(parsed)) {
      setMinInputVal(String(currentMin))
      return
    }
    if (parsed < min) {
      setMinInputVal(String(min))
      onValueChange?.([min, currentMax])
      return
    }
    if (parsed > max) {
      setMinInputVal(String(max))
      onValueChange?.([max, currentMax])
      return
    }
    if (parsed > currentMax) {
      setMinInputVal(String(currentMin))
      return
    }
    onValueChange?.([parsed, currentMax])
  }

  const commitMax = () => {
    const parsed = Number(maxInputVal)
    if (maxInputVal.trim() === "" || isNaN(parsed)) {
      setMaxInputVal(String(currentMax))
      return
    }
    if (parsed > max) {
      setMaxInputVal(String(max))
      onValueChange?.([currentMin, max])
      return
    }
    if (parsed < min) {
      setMaxInputVal(String(min))
      onValueChange?.([currentMin, min])
      return
    }
    if (parsed < currentMin) {
      setMaxInputVal(String(currentMax))
      return
    }
    onValueChange?.([currentMin, parsed])
  }

  const handleKeyDown = (e: React.KeyboardEvent<HTMLInputElement>, commit: () => void) => {
    if (e.key === "Enter") commit()
  }

  const inputCls = "text-xs text-card-foreground tabular-nums border border-border rounded px-1.5 py-0.5 text-center outline-none focus:border-primary bg-transparent min-w-[2ch]"

  const inputWidth = (val: string) => ({ width: `${Math.max(val.length, 2) + 1.5}ch` })

  return (
    <div className="flex flex-col gap-1.5 w-full font-[var(--font-family--sans)]">
      {(label || showValue) && (
        <div className="flex items-center justify-between">
          {label && (
            <span className={cn("text-sm font-medium text-foreground", disabled && "text-muted-foreground")}>
              {label}
            </span>
          )}
          {showValue && (
            <span className={cn("text-sm text-muted-foreground tabular-nums", disabled && "text-muted-foreground/60")}>
              {Array.isArray(currentValue) && (currentValue as number[]).length === 2
                ? `${(currentValue as number[])[0]} - ${(currentValue as number[])[1]}`
                : (currentValue as number[])[0]}
            </span>
          )}
        </div>
      )}

      <div className="flex flex-col w-full gap-2.5">
        <div className={cn("relative w-full", showTooltip && "mt-7")}>
          {showTooltip && (currentValue as number[]).map((v, i) => (
            <div
              key={i}
              className="absolute -top-7 flex flex-col items-center pointer-events-none"
              style={{ left: `${thumbPct(v)}%`, transform: "translateX(-50%)" }}
            >
              <span className={cn(
                "rounded px-1.5 py-0.5 text-xs font-semibold text-white leading-tight",
                disabled ? "bg-muted-foreground" : "bg-foreground"
              )}>
                {v}
              </span>
            </div>
          ))}

          <SliderPrimitive.Root
            ref={ref}
            disabled={disabled}
            value={value}
            defaultValue={defaultValue}
            min={min}
            max={max}
            onValueChange={onValueChange}
            className={cn(sliderVariants({ size }), "px-1", className)}
            {...props}
          >
            <SliderPrimitive.Track
              data-slot="track"
              className="relative grow overflow-hidden rounded-full bg-muted w-full"
            >
              <SliderPrimitive.Range className={cn(
                "absolute h-full rounded-full",
                disabled ? "bg-muted-foreground" : "bg-primary"
              )} />
            </SliderPrimitive.Track>

            {min !== max && (currentValue as number[]).map((_, i) => (
              <SliderPrimitive.Thumb
                key={i}
                className={cn(
                  "block rounded-full bg-white shadow-sm",
                  "border-1 border-primary",
                  "ring-offset-white transition-all",
                  "focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2",
                  "disabled:pointer-events-none",
                  size === "sm" && "h-2.5 w-2.5",
                  (size === "md" || !size) && "h-3.5 w-3.5",
                  size === "lg" && "h-6 w-6",
                  disabled && "border-border"
                )}
              />
            ))}
          </SliderPrimitive.Root>
        </div>

        {showMinMax && (
          <div className="flex justify-between">
            <input
              type="text"
              inputMode="numeric"
              value={minInputVal}
              onChange={handleMinChange}
              onBlur={commitMin}
              onKeyDown={(e) => handleKeyDown(e, commitMin)}
              className={inputCls}
              style={inputWidth(minInputVal)}
              aria-label="Minimum value"
            />
            <input
              type="text"
              inputMode="numeric"
              value={maxInputVal}
              onChange={handleMaxChange}
              onBlur={commitMax}
              onKeyDown={(e) => handleKeyDown(e, commitMax)}
              className={inputCls}
              style={inputWidth(maxInputVal)}
              aria-label="Maximum value"
            />
          </div>
        )}
      </div>
    </div>
  )
})
Slider.displayName = SliderPrimitive.Root.displayName

export { Slider, sliderVariants }